import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FileText, Download, FileImage } from "lucide-react";

/**
 * Admin-only viewer for a user's uploaded PSA/SIA/qualification documents.
 * Uses /api/admin/users/{userId}/documents to list, then hits /api/files/{id}
 * with the admin's bearer token (auth query param) to open the file.
 */
export default function DocumentViewer({ user }) {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/users/${user.id}/documents`);
      setDocs(data.documents || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openDoc = (id) => {
    const token = localStorage.getItem("ss_token") || "";
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const url = `${base}/api/files/${id}?auth=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="border-white/15" data-testid={`admin-docs-btn-${user.secusource_id}`}>
          <FileText className="w-3.5 h-3.5 mr-1" /> Documents ({(user.documents || []).length})
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Documents · {user.secusource_id}</DialogTitle></DialogHeader>
        {busy && <div className="text-white/60 font-manrope text-sm py-4">Loading…</div>}
        {!busy && docs.length === 0 && (
          <div className="text-white/60 font-manrope text-sm py-4">
            No documents uploaded yet.
          </div>
        )}
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-3 border border-white/10 rounded-md p-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-outfit font-semibold text-white truncate">
                  {(d.content_type || "").startsWith("image/") ? <FileImage className="w-4 h-4 text-brand flex-shrink-0" /> : <FileText className="w-4 h-4 text-brand flex-shrink-0" />}
                  <span className="truncate">{d.original_filename}</span>
                </div>
                <div className="text-[11px] text-white/60 font-manrope">{d.doc_type} · {Math.round((d.size || 0) / 1024)} KB · {new Date(d.created_at).toLocaleDateString()}</div>
              </div>
              <Button size="sm" onClick={() => openDoc(d.id)} className="bg-brand hover:bg-brand-hover text-white" data-testid={`admin-doc-open-${d.id}`}>
                <Download className="w-3.5 h-3.5 mr-1" /> View
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
